/**
 * Notification row copy.
 *
 * The API stores each notification as one prebuilt sentence ("Task \"Vynterface:
 * Ability to Suspend a Business was removed\" moved to In Progress"), which is
 * fine in an email and unreadable in a narrow feed row: the interesting part —
 * what happened — sits at the end and gets truncated away.
 *
 * These helpers re-split that into two lines using fields the payload already
 * carries: the event ("Moved to In Progress") on top, the record it happened to
 * ("TKT-181 · STAFF - Cancellations") underneath. Types whose payload carries
 * nothing but the sentence fall back to the sentence, unchanged.
 *
 * Mirrors apps/mobile/src/utils/notification-copy.ts — both consume the same
 * `Notification` shape from @scrambl/shared, so the two must stay in step.
 */
import type { Notification } from '@scrambl/shared'

export interface NotificationRowCopy {
  /** Concise event phrase — the semibold first line. */
  primary: string
  /** Record identifier + title, or null when the payload names no record. */
  secondary: string | null
}

const asData = (notification: Notification): Record<string, unknown> =>
  notification.data && typeof notification.data === 'object'
    ? (notification.data as Record<string, unknown>)
    : {}

const readText = (
  data: Record<string, unknown>,
  ...keys: string[]
): string | null => {
  for (const key of keys) {
    const value = data[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value))
      return String(value)
  }
  return null
}

/**
 * "Task \"Some name\" moved to In Progress" → the status half. Only consulted
 * for project-watch rows, where that exact shape is what the API writes, and
 * only when `data.statusName` is missing (older rows predate it).
 */
const parseMovedToStatus = (title: string): string | null => {
  const marker = ' moved to '
  const index = title.lastIndexOf(marker)
  if (index < 0) return null
  const status = title.slice(index + marker.length).trim()
  return status.length > 0 && status.length <= 40 ? status : null
}

/** Same shape, the quoted record name half. */
const parseQuotedName = (title: string): string | null => {
  const match = /"([^"]+)"/.exec(title)
  const name = match?.[1]?.trim()
  return name && name.length > 0 ? name : null
}

/**
 * A status arrives either as a human-authored label ("In Progress", "STAFF
 * Review") or as a raw stage slug ("in-progress", "open") when no named stage
 * row backs it. Title-case the slugs; anything already carrying an uppercase
 * letter is someone's chosen wording and is left exactly as written.
 */
const formatStatusName = (status: string | null): string | null => {
  if (!status) return null
  if (/[A-Z]/.test(status)) return status
  const words = status
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
  return words.length > 0 ? words.join(' ') : status
}

/** Ticket event phrases. `status` is the ticket's stage name when known. */
const getTicketEvent = (type: string, status: string | null): string | null => {
  switch (type) {
    case 'TICKET_CREATED':
      return 'New ticket'
    case 'TICKET_ASSIGNED':
      return 'Assigned to you'
    case 'TICKET_FOLLOWED':
      return 'You’re following this'
    case 'TICKET_STATUS_CHANGED':
      return status ? `Moved to ${status}` : 'Status changed'
    case 'TICKET_COMMENT_ADDED':
      return 'New comment'
    case 'TICKET_CUSTOMER_REPLY':
      return 'Customer replied'
    case 'TICKET_UPDATED':
      return 'Ticket updated'
    default:
      return null
  }
}

/** Task event phrases. `status` is the task's new status name when known. */
const getTaskEvent = (type: string, status: string | null): string | null => {
  switch (type) {
    case 'TASK_ASSIGNED':
      return 'Assigned to you'
    case 'TASK_STATUS_CHANGED':
      return status ? `Moved to ${status}` : 'Status changed'
    case 'TASK_PRIORITY_CHANGED':
      return 'Priority changed'
    case 'TASK_COMMENT_ADDED':
      return 'New comment'
    case 'TASK_DUE_SOON':
      return 'Due soon'
    case 'TASK_OVERDUE':
      return 'Overdue'
    case 'TASK_SLA_BREACHED':
      return 'SLA breached'
    default:
      return null
  }
}

/** Project-watch rows carry the real event in `data.watchEvent`. */
const getWatchEvent = (
  watchEvent: string | null,
  status: string | null,
): string => {
  switch (watchEvent) {
    case 'TASK_STATUS_CHANGED':
      return status ? `Moved to ${status}` : 'Status changed'
    case 'TASK_COMPLETED':
      return 'Completed'
    case 'TASK_REOPENED':
      return 'Reopened'
    case 'TASK_PRIORITY_CHANGED':
      return 'Priority changed'
    case 'TASK_DUE_DATE_CHANGED':
      return 'Due date changed'
    case 'TASK_RENAMED':
      return 'Renamed'
    case 'TASK_CREATED':
      return 'New task'
    case 'TASK_DELETED':
      return 'Task deleted'
    default:
      return 'Task activity'
  }
}

const joinRecord = (identifier: string | null, title: string | null) =>
  [identifier, title].filter(Boolean).join(' · ') || null

/**
 * Two-line copy for an activity row. Falls back to the stored sentence for any
 * type whose payload cannot describe the event on its own — better a long title
 * than a wrong one.
 */
export function getNotificationRowCopy(
  notification: Notification,
): NotificationRowCopy {
  const data = asData(notification)
  const type = String(notification.type)
  const fallback: NotificationRowCopy = {
    primary: notification.title,
    secondary: null,
  }

  const ticketNumber = readText(data, 'ticketNumber')
  const ticketSubject = readText(data, 'ticketSubject')
  if (ticketNumber || ticketSubject) {
    const event = getTicketEvent(
      type,
      formatStatusName(readText(data, 'ticketStatus')),
    )
    if (!event) return fallback
    return {
      primary: event,
      secondary: joinRecord(ticketNumber, ticketSubject),
    }
  }

  if (type === 'PROJECT_WATCH_TASK_ACTIVITY') {
    const status = formatStatusName(
      readText(data, 'statusName') ?? parseMovedToStatus(notification.title),
    )
    const taskName =
      readText(data, 'taskName') ?? parseQuotedName(notification.title)
    return {
      primary: getWatchEvent(readText(data, 'watchEvent'), status),
      secondary: joinRecord(readText(data, 'taskNumber'), taskName),
    }
  }

  const taskName = readText(data, 'taskName')
  if (taskName) {
    const event = getTaskEvent(
      type,
      formatStatusName(readText(data, 'statusName', 'newStatus')),
    )
    if (!event) return fallback
    return {
      primary: event,
      secondary: joinRecord(readText(data, 'taskNumber'), taskName),
    }
  }

  // Everything else (timesheets, imports, digests, system notices) already
  // reads as a short sentence — leaving it alone is the safe default.
  return fallback
}
